import { AppDispatch, RootState } from "@Reducers/index";
import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import { setUserTagLoading, setUserTagData, setUpdatedUserProfileDetails } from "../reducers/userTagData";
import type { GetUserDetailsByIdRequest, GetUserDetailsByIdResponse } from "@ServerTypes/accounts/accounts.schema";
import type { Exact } from "@Helpers/types/exact";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

export const fetchUserTagData = (id: ObjectIdString) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { userTagData } = getState();

  if (userTagData[id]?.isLoading || userTagData[id]?.isLoaded) {
    return;
  }

  dispatch(setUserTagLoading({ id }));

  try {
    // Fetch user details
    const response = await getUserDetailsById({ userId: id });
    let profilePic: string | ArrayBuffer | null = null;

    // If there's a profile picture to fetch, get the image as a blob
    // SCHEMA GAP: the getUserDetailsById response is the opaque status envelope and does not
    // declare the `data` payload the handler really returns. Name kept verbatim (it emits).
    const responseDataAny = response.data as typeof response.data & {
      data: { profilePic?: string } & Record<string, unknown>;
    };
    if (responseDataAny?.data.profilePic) {
      const imgResponse = await axiosWrapper.get<Blob>(`${getBackendUrl()}/fileSystemServer/getImage/profile/${id}`, {
        responseType: "blob",
      });

      // Use FileReader to convert blob to Base64
      const reader = new FileReader();

      reader.onloadend = function () {
        // The result of reader is a Base64 string
        profilePic = reader.result;

        // Dispatch the data with the Base64 image URL
        // `profilePic` is declared as the FileReader result (`string | ArrayBuffer | null`), but
        // `UserTagProfileData.profilePic` is a string — readAsDataURL only ever yields a string.
        dispatch(setUserTagData({ id, data: { ...(responseDataAny?.data || {}), profilePic: profilePic as string } }));
      };

      // Read the Blob as Data URL
      reader.readAsDataURL(imgResponse.data);
    } else {
      // If there's no profile picture, just dispatch the data without it
      dispatch(setUserTagData({ id, data: { ...(responseDataAny?.data || {}), profilePic } }));
    }
  } catch (error) {
    console.error("Error fetching user tag data:", error);
    Promise.reject(error);
  }
};

export const updateUserProfileDetails = (data: Record<string, unknown>) => (dispatch: AppDispatch) => {
  if (data) {
    dispatch(setUpdatedUserProfileDetails(data));
  }
};

export async function getUserDetailsById<T extends Exact<GetUserDetailsByIdRequest, T>>(data: T) {
  return await axiosWrapper.post<GetUserDetailsByIdResponse>(`${getBackendUrl()}/accounts/getUserDetailsById`, data);
}
